/**
 * Multi-org On/Off toggle that relocates each org's own payer prompt.
 *
 * Unlike upload/sync, this never copies another org's payload — it only moves
 * whatever each target already has between top level and `disabled`.
 * @module togglePayerAcrossOrgs
 */
import { AuthLetterAgentConfigService } from "../../../../api/services/authLetterAgentConfigService";
import { buildToggleFields, Dict } from "./authLetterConfigUtils";

export interface TogglePayerAcrossOrgsResult {
  /** Orgs that were successfully toggled. */
  updatedOrgIds: string[];
  /** Orgs with nothing to move for this payer. */
  skippedOrgIds: string[];
  /** Orgs whose read/write failed. */
  failures: { orgId: string; message: string }[];
  /** Merged primary-org doc when the primary was among successful updates. */
  primaryNextDoc: Dict | null;
}

/**
 * Toggles a payer On/Off across orgs using each org's existing prompt content.
 *
 * @param params.payer - Payer to relocate
 * @param params.enabled - Desired enabled state
 * @param params.targetOrgIds - Orgs to update
 * @param params.primaryOrgId - Org whose local UI doc is mirrored
 * @param params.primaryDoc - Current in-memory primary org config
 * @return Per-org outcomes and optional primary next doc
 */
export const togglePayerAcrossOrgs = async (params: {
  payer: string;
  enabled: boolean;
  targetOrgIds: string[];
  primaryOrgId: string;
  primaryDoc: Dict;
}): Promise<TogglePayerAcrossOrgsResult> => {
  const { payer, enabled, primaryOrgId, primaryDoc } = params;
  const uniqueIds = Array.from(
    new Set(params.targetOrgIds.map((id) => id.trim()).filter(Boolean)),
  );

  const failures: { orgId: string; message: string }[] = [];
  const skippedOrgIds: string[] = [];
  const updatedOrgIds: string[] = [];
  let primaryNextDoc: Dict | null = null;

  await Promise.all(
    uniqueIds.map(async (targetId) => {
      try {
        const orgDoc =
          targetId === primaryOrgId
            ? primaryDoc
            : await AuthLetterAgentConfigService.getRawConfig(targetId);
        const built = buildToggleFields(orgDoc, payer, enabled);
        if (!built) {
          skippedOrgIds.push(targetId);
          return;
        }
        const now = new Date().toISOString();
        built.fields.updated_at = now;
        built.nextDoc.updated_at = now;
        await AuthLetterAgentConfigService.updatePayerFields(
          targetId,
          built.fields,
        );
        updatedOrgIds.push(targetId);
        if (targetId === primaryOrgId) primaryNextDoc = built.nextDoc;
      } catch (e) {
        failures.push({
          orgId: targetId,
          message: e instanceof Error ? e.message : "Unknown error",
        });
      }
    }),
  );

  return { updatedOrgIds, skippedOrgIds, failures, primaryNextDoc };
};
